import React, { forwardRef, useCallback } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import tw from 'twin.macro';
import Disclosure from 'components/Disclosure';
import { AtelierDisclosureButton } from 'containers/CahierJournal/EventModal/ModalBody/Ateliers/AtelierDisclosureButton';
import { AtelierDisclosurePanel } from 'containers/CahierJournal/EventModal/ModalBody/Ateliers/AtelierDisclosurePanel';
import Button from 'components/Buttons/Button';
import DragHandleVertical from 'components/Icons/icons/DragHandleVertical';
import { CJAtelier } from 'types/cj';
import { Eleve } from 'types/eleves';
import { EleveGroup } from 'types/eleveGroups';
import { TrashIcon } from '@heroicons/react/24/outline';

interface SortableAtelierProps {
  atelier: CJAtelier;
  disableGroups?: boolean;
  eleves: Eleve[];
  groups: EleveGroup[];
  onCreateGroup?: (g: EleveGroup) => Promise<any>;
  onDeleteGroup?: (id: string) => Promise<any>;
  onUpdateGroup?: (g: EleveGroup) => Promise<any>;
  onChange: (newValue: CJAtelier) => void;
  onDelete: () => void;
  defaultOpen?: boolean;
  ref?: React.Ref<HTMLDivElement>;
}

export const SortableAtelier = forwardRef<HTMLDivElement, SortableAtelierProps>(
  function SortableAtelier(props, forwardedRef) {
    const {
      atelier,
      eleves,
      groups,
      onCreateGroup,
      onDeleteGroup,
      onUpdateGroup,
      onChange,
      onDelete,
      defaultOpen,
    } = props;

    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ id: atelier.id });

    // Merge the @dnd-kit ref + the forwarded ref:
    const combinedRef = useCallback(
      (node: HTMLDivElement | null) => {
        setNodeRef(node);

        if (!forwardedRef) return;
        if (typeof forwardedRef === 'function') {
          forwardedRef(node);
        } else {
          (
            forwardedRef as React.MutableRefObject<HTMLDivElement | null>
          ).current = node;
        }
      },
      [forwardedRef, setNodeRef],
    );

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
    };

    return (
      <Disclosure fill defaultOpen={defaultOpen}>
        <div
          ref={combinedRef}
          style={style}
          css={[tw`flex items-center space-x-2`, isDragging && tw`opacity-50`]}
        >
          <div tw="flex flex-col flex-grow bg-white rounded-lg">
            <div tw="flex flex-grow rounded-lg overflow-hidden!">
              <Disclosure.Button
                alignText="left"
                iconPosition="right"
                iconRotation="bottom-to-top"
                aria-label={`Toggle atelier ${atelier.title}`}
                fill
                large
                tw="rounded-none border-none min-w-0"
              >
                <div tw="flex flex-grow items-center gap-x-2">
                  <div {...attributes} {...listeners}>
                    <Button minimal tw="px-2">
                      <DragHandleVertical tw="h-5 w-5" />
                    </Button>
                  </div>
                  <AtelierDisclosureButton
                    disableGroups={props.disableGroups}
                    eleves={eleves}
                    onDeleteGroup={onDeleteGroup}
                    onCreateGroup={onCreateGroup}
                    onUpdateGroup={onUpdateGroup}
                    atelier={atelier}
                    groups={groups}
                    onChange={onChange}
                    defaultOpen={defaultOpen}
                  />
                </div>
              </Disclosure.Button>
            </div>
            <AtelierDisclosurePanel atelier={atelier} onChange={onChange} />
          </div>
          <Button
            intent="danger"
            minimal
            tw="whitespace-nowrap"
            onClick={onDelete}
            icon={TrashIcon}
          />
        </div>
      </Disclosure>
    );
  },
);
